import { useEffect, useRef, useState } from "react";
import { cn } from "@utils";
import AiSparkleIcon from "../SmartNoteModal/AiSparkleIcon";
import { SECTION_STAGGER_MS, SUGGESTION_FIRST_ROW_DELAY_MS } from "./ReadBlock";

// Grey base with an AI-purple band in the middle; the shimmer animation sweeps it through the label.
const SHIMMER_GRADIENT =
  "linear-gradient(90deg, #a1a1aa 0%, #a1a1aa 38%, #8b5cf6 46%, #6940ff 50%, #8b5cf6 54%, #a1a1aa 62%, #a1a1aa 100%)";

// The label focuses in first, then each suggestion reveals one-at-a-time beneath it with the Ontik read's
// own animation and cadence, so the rows read as the same wave continuing down the page.
const LABEL_DELAY_MS = 20;
const FIRST_ROW_DELAY_MS = SUGGESTION_FIRST_ROW_DELAY_MS;
const ROW_STAGGER_MS = SECTION_STAGGER_MS;

// Model suggestions take a second or two, so the wait is framed as generation rather than a lookup. The
// loader holds off briefly so a fast response never flashes it, and once shown stays long enough not to flicker.
const LOADER_DELAY_MS = 250;
const LOADER_MIN_VISIBLE_MS = 500;
const SKELETON_WIDTHS = ["w-4/6", "w-1/2", "w-5/6"];

// "generating" when nothing is on screen yet (skeleton rows replace the list), "refreshing" when a previous
// set is showing (kept, dimmed), null otherwise. The mode is latched while the loader is visible so a set
// arriving mid-way doesn't cut it short.
export function useSuggestionLoader(loading, empty) {
  const [mode, setMode] = useState(null);
  const shownAt = useRef(0);
  const emptyRef = useRef(empty);
  emptyRef.current = empty;

  useEffect(() => {
    if (loading && mode === null) {
      const timer = setTimeout(() => {
        shownAt.current = Date.now();
        setMode(emptyRef.current ? "generating" : "refreshing");
      }, LOADER_DELAY_MS);
      return () => clearTimeout(timer);
    }
    if (!loading && mode !== null) {
      const remaining = LOADER_MIN_VISIBLE_MS - (Date.now() - shownAt.current);
      const timer = setTimeout(() => setMode(null), Math.max(0, remaining));
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [loading, mode]);

  return mode;
}

function LoadingLabel({ children, className }) {
  return (
    <span
      role="status"
      className={cn(
        "inline-block whitespace-nowrap bg-clip-text text-transparent animate-suggestionLoading",
        "motion-reduce:animate-none motion-reduce:bg-none motion-reduce:text-zinc-400",
        className,
      )}
      style={{ backgroundImage: SHIMMER_GRADIENT, backgroundSize: "200% auto" }}
    >
      {children}
    </span>
  );
}

// The composer's "SUGGESTED" autocomplete list. One row is "active" at a time, driven only by the
// composer's arrow keys (activeIndex) — hovering never ghosts into the composer, like Google's search
// box. Clicking a row sends it (onAccept);
// Enter sends the active row and Tab fills it — both handled in the composer. The active row is
// highlighted and ghosts into the composer. Buttons use onMouseDown + preventDefault so a click never
// pulls focus off the composer.
export default function SuggestionPanel({
  suggestions,
  activeIndex = -1,
  onAccept,
  loaderMode = null,
}) {
  const hasSuggestions = suggestions && suggestions.length > 0;
  if (!hasSuggestions && loaderMode !== "generating") return null;

  if (loaderMode === "generating") {
    return (
      <div className="mt-3 border-t border-zinc-100 pt-3">
        <div className="flex items-center gap-1.5 px-1 animate-suggestionFadeIn motion-reduce:animate-none">
          <AiSparkleIcon className="size-3.5 shrink-0" />
          <LoadingLabel className="text-[13px] font-medium leading-none">
            Generating your personalised recommendations…
          </LoadingLabel>
        </div>
        <ul className="mt-1.5 flex flex-col" aria-hidden="true">
          {SKELETON_WIDTHS.map((width, index) => (
            <li
              key={width}
              className="flex h-9 items-center px-2.5 animate-readIn motion-reduce:animate-none"
              style={{ animationDelay: `${FIRST_ROW_DELAY_MS + index * ROW_STAGGER_MS}ms` }}
            >
              <span className={cn("h-3 animate-pulse rounded-full bg-zinc-100", width)} />
            </li>
          ))}
        </ul>
      </div>
    );
  }

  const refreshing = loaderMode === "refreshing";

  return (
    <div className="mt-3 border-t border-zinc-100 pt-3">
      {refreshing ? (
        <div className="flex items-center gap-1.5 px-1">
          <LoadingLabel className="text-[11px] font-medium uppercase leading-none tracking-wider">
            Updating recommendations…
          </LoadingLabel>
          <AiSparkleIcon className="size-3.5 shrink-0" />
        </div>
      ) : (
        <div
          className="flex items-center gap-1.5 px-1 animate-suggestionFadeIn motion-reduce:animate-none"
          style={{ animationDelay: `${LABEL_DELAY_MS}ms` }}
        >
          {/* Clean grey base (always visible), with an AI-purple shimmer overlaid that fades in and sweeps
              through only during the ~2s twinkle every 30s — so the label never looks washed-out at rest.
              Both copies are block + leading-none so the overlay sits exactly on the base. */}
          <span className="relative inline-block leading-none">
            <span className="block text-[11px] font-medium uppercase leading-none tracking-wider text-zinc-400">
              Suggested
            </span>
            <span
              aria-hidden="true"
              className="animate-suggestedShimmer pointer-events-none absolute inset-0 block whitespace-nowrap bg-clip-text text-[11px] font-medium uppercase leading-none tracking-wider text-transparent opacity-0 motion-reduce:hidden"
              style={{
                backgroundImage: SHIMMER_GRADIENT,
                backgroundSize: "200% auto",
                backgroundRepeat: "no-repeat",
              }}
            >
              Suggested
            </span>
          </span>
          <AiSparkleIcon className="size-3.5 shrink-0 opacity-0 animate-suggestedStar motion-reduce:hidden" />
        </div>
      )}

      <ul className={cn("mt-1.5 flex flex-col transition-opacity duration-300", refreshing && "opacity-60")}>
        {suggestions.map((suggestion, index) => {
          const isActive = index === activeIndex;
          return (
            <li
              key={suggestion.id ?? index}
              className="animate-readIn motion-reduce:animate-none"
              style={{ animationDelay: `${FIRST_ROW_DELAY_MS + index * ROW_STAGGER_MS}ms` }}
            >
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onAccept(suggestion)}
                className={cn(
                  "group flex w-full items-center rounded-lg px-2.5 py-2 text-left",
                  "transition-[background-color,transform] duration-200 ease-out",
                  "active:scale-[0.99] active:duration-100 motion-reduce:transform-none",
                  isActive ? "bg-zinc-100" : "hover:bg-zinc-100/70",
                )}
              >
                <span
                  className={cn(
                    "min-w-0 flex-1 truncate text-sm transition-colors duration-200",
                    isActive ? "text-zinc-900" : "text-zinc-700",
                  )}
                >
                  {suggestion.promptText}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
